import { AbsenceListDto, AbsenceType, ResourceType } from '@lmt/shared-types';
import { ResourceInfo } from '@lmt/unicorn-shared/app/components/absences/popup/ResourceInfo';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import AbsenceTypeIcon from '@lmt/unicorn-shared/app/icons/absence_type.svg?react';
import CalendarIcon from '@lmt/unicorn-shared/app/icons/calendar-event.svg?react';
import EquipmentIcon from '@lmt/unicorn-shared/app/icons/equipment.svg?react';
import HolidayIcon from '@lmt/unicorn-shared/app/icons/holiday.svg?react';
import DoctorIcon from '@lmt/unicorn-shared/app/icons/persons.svg?react';
import RoomIcon from '@lmt/unicorn-shared/app/icons/room-resource.svg?react';
import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import styles from '@lmt/unicorn-shared/src/app/components/absences/popup/AbsencePopup.module.scss';
import React, { FC, useCallback, useMemo } from 'react';
import { isEqual, startOfDay, subDays } from 'date-fns';
import { useEventCallback } from '@mui/material';
import { useSortedResources } from '@lmt/unicorn-shared/app/api/custom-hooks';

interface Props {
  date: Date | null;
  events: AbsenceListDto[];
  handleClose?: () => void;
}

export const AbsencePopup: FC<Props> = (props) => {
  const i18next = useScopedTranslation('absences');
  const forms = useGlobalForms();
  const claims = useClaims();
  const { events, date, handleClose } = props;

  const eventsByType = useMemo(() => {
    return {
      holidays: events
        .filter((x) => x.absenceType === AbsenceType.Holiday)
        .sort((a, b) =>
          a.title.toLowerCase() > b.title.toLowerCase()
            ? 1
            : b.title.toLowerCase() > a.title.toLowerCase()
              ? -1
              : 0,
        ),
      absences: events.filter(
        (x) => x.absenceType === AbsenceType.ResourceUnavailability,
      ),
    };
  }, [events]);

  const resourceIds = useMemo(() => {
    let resourceIds: number[] = [];
    eventsByType.absences.forEach((x) => resourceIds.push(...x.resourceIds));
    resourceIds = [...new Set(resourceIds)];
    return resourceIds;
  }, [eventsByType]);
  const places = useSortedResources(ResourceType.Places, true, resourceIds);
  const devices = useSortedResources(
    ResourceType.SpecialConditions,
    true,
    resourceIds,
  );
  const doctors = useSortedResources(ResourceType.Doctors, true, resourceIds);

  const onResourceClick = useEventCallback((resourceId: number) => {
    handleClose?.();
    const absence = eventsByType.absences.find((x) =>
      x.resourceIds.includes(resourceId),
    );
    forms.openAbsenceEditing(absence!.id);
  });

  const HolidayInfo = useCallback(() => {
    return (
      <div>
        {eventsByType.holidays.map((holiday) => {
          return (
            <div className={styles.absenceInfoContainer} key={holiday.id}>
              <div className={styles.row}>
                <HolidayIcon className={styles.holidayIcon} />
                <span className={styles.holidayTitle}>
                  {i18next.t('holiday')}
                </span>
              </div>
              <div className={styles.lowerRow}>
                <CalendarIcon className={styles.calendarIcon} />
                <UniLink
                  color={ButtonColor.Primary}
                  className={styles.rowTitle}
                  title={holiday.title}
                  onClick={() => {
                    forms.openAbsenceEditing(holiday.id);
                    handleClose?.();
                  }}
                  disabled={!claims.permissions.manageAbsences}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [eventsByType, handleClose]);

  const ResourcesInfo = useCallback(() => {
    if (!places.length && !devices.length) {
      return null;
    }
    return (
      <div className={styles.absenceInfoContainer}>
        <div className={styles.row}>
          <AbsenceTypeIcon className={styles.absenceIcon} />
          <span className={styles.absenceTitle}>
            {i18next.t('resource_unavailable')}
          </span>
        </div>
        <ResourceInfo
          resourceIcon={<RoomIcon className={styles.resourceIcon} />}
          resources={places}
          onItemClick={onResourceClick}
        />
        <ResourceInfo
          resourceIcon={<EquipmentIcon className={styles.resourceIcon} />}
          resources={devices}
          onItemClick={onResourceClick}
        />
      </div>
    );
  }, [eventsByType, places, devices, onResourceClick]);

  const isHoliday = useMemo(() => {
    return !!eventsByType.holidays.length;
  }, [eventsByType]);

  const isAbsence = useMemo(() => {
    return !!eventsByType.absences.length;
  }, [eventsByType]);

  const DoctorInfo = useCallback(() => {
    if (!doctors.length) {
      return null;
    }
    return (
      <div className={styles.absenceInfoContainer}>
        <div className={styles.row}>
          <AbsenceTypeIcon className={styles.absenceIcon} />
          <span className={styles.absenceTitle}>
            {i18next.t('doctor_unavailable')}
          </span>
        </div>
        <ResourceInfo
          resourceIcon={<DoctorIcon className={styles.resourceIcon} />}
          resources={doctors}
          onItemClick={onResourceClick}
        />
      </div>
    );
  }, [eventsByType, doctors]);
  const firstEvent = events[0];
  if (!firstEvent) return null;

  // if end date is 00:00 of next day - we should subtract 1 day.
  // otherwise, use end date as is
  const startOfEndDate = startOfDay(firstEvent.end);
  const secondDate = isEqual(firstEvent.end, startOfEndDate)
    ? subDays(startOfEndDate, 1)
    : startOfEndDate;
  return (
    <div className={styles.appointmentContainer}>
      <div className={styles.titleContainer}>
        <span className={styles.title}>
          {date
            ? localFormat(date, 'LLLL dd')
            : firstEvent.start >= secondDate
              ? localFormat(firstEvent.start, 'LLLL dd')
              : `${localFormat(firstEvent.start, 'LLLL dd')} - ${localFormat(
                  secondDate,
                  'LLLL dd',
                )}`}
        </span>
      </div>
      <HolidayInfo />
      {isHoliday && isAbsence && <div className={styles.separator}></div>}
      <DoctorInfo />
      {!!doctors.length && (!!places.length || !!devices.length) && (
        <div className={styles.separator}></div>
      )}
      <ResourcesInfo />
    </div>
  );
};
